import {
  ArrowDown,
  ArrowUp,
  AudioLinesIcon,
  ChevronDown,
  ChevronUp,
  FileIcon,
  ImageIcon,
  Trash2,
  VideoIcon,
} from "lucide-react";
import { FC, PropsWithChildren, useCallback, useReducer } from "react";

import { Button } from "@/components/ui/button";
import { FileType, MediaFileType } from "@/lib/uploads";
import { cn } from "@/lib/utils";

type Props = PropsWithChildren<{
  index: number;
  title: string;
  description: string;
  thumbnailUrl?: string;
  onDelete(): void;
  mediaType: MediaFileType;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove(fromIndex: number, toIndex: number): void;
  isDownloadable?: boolean;
}>;

const getMediaItemIcon = (mediaType: MediaFileType) => {
  switch (mediaType) {
    case FileType.IMAGE:
      return <ImageIcon className="size-6 text-white" />;
    case FileType.AUDIO:
      return <AudioLinesIcon className="size-6 text-white" />;
    case FileType.VIDEO:
      return <VideoIcon className="size-6 text-white" />;
    default:
      return <FileIcon className="size-6 text-white" />;
  }
};

const MediaItem: FC<Props> = ({
  index,
  title,
  description,
  thumbnailUrl,
  mediaType,
  onDelete,
  children,
  canMoveUp,
  canMoveDown,
  onMove,
  isDownloadable,
}) => {
  const [isExpanded, toggleExpanded] = useReducer((state) => !state, false);

  const handleMoveUp = useCallback(() => {
    onMove(index, index - 1);
  }, [onMove, index]);

  const handleMoveDown = useCallback(() => {
    onMove(index, index + 1);
  }, [onMove, index]);

  return (
    <div className="rounded-lg overflow-hidden">
      <div className="bg-gray-100 flex px-3 justify-between">
        <div className="flex flex-1 items-center">
          <button
            type="button"
            onClick={toggleExpanded}
            className="flex flex-1 gap-2 items-center py-3 pr-2"
          >
            <div
              className={cn(
                "size-13 min-w-13 rounded-sm flex items-center justify-center overflow-hidden",
                thumbnailUrl ? "bg-transparent" : "bg-[hsla(0,0%,0%,0.25)]",
              )}
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt="thumbnail"
                  className="size-full object-cover"
                />
              ) : (
                getMediaItemIcon(mediaType)
              )}
            </div>
            <div className="text-left">
              <p className="text-sm line-clamp-1 break-all">{title}</p>
              <p className="text-sm text-muted-foreground line-clamp-1 break-all">
                {description}
              </p>
            </div>
          </button>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex gap-1 items-center">
            <Button
              type="button"
              size="icon"
              variant="outline"
              disabled={!canMoveUp}
              onClick={handleMoveUp}
            >
              <ArrowUp />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              disabled={!canMoveDown}
              onClick={handleMoveDown}
            >
              <ArrowDown />
            </Button>
          </div>
          <div>
            <p className="text-sm text-muted-foreground line-clamp-1 break-all">
              Download {isDownloadable ? "ON" : "OFF"}
            </p>
          </div>
          <Button type="button" size="icon" variant="ghost" onClick={onDelete}>
            <Trash2 className="size-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={toggleExpanded}
            type="button"
            className="bg-gray-200 w-26"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="size-4" />
                Collapse
              </>
            ) : (
              <>
                <ChevronDown className="size-4" />
                Expand
              </>
            )}
          </Button>
        </div>
      </div>

      {isExpanded && <div>{children}</div>}
    </div>
  );
};

export default MediaItem;
